import { Undo } from 'lucide-react';
import { useMemo } from 'react';
import { useMap } from 'react-leaflet';

import { DEFAULT_ZOOM, POSITION_CLASSES } from '@/constants';
import { useIsPointVisible } from '@/hooks/useIsPointVisible';

const ResetViewControl = ({ position = 'topright', bounds, center, zoom = DEFAULT_ZOOM, className = '' }: {
  position?: keyof typeof POSITION_CLASSES,
  bounds?: [number, number][],
  center?: [number, number],
  zoom?: number,
  className?: string,
}) => {
  const map = useMap();
  const controlClassName = `${POSITION_CLASSES[position]} ${className}`;

  if (!bounds && !center) {
    throw new Error('Either `bounds` or `center` must be provided');
  }

  // If `bounds` is provided, that is what we want to reset to; for
  // the purposes of button visbility, we still may need a `center`
  // parameter, so we calculate it.
  // If only `center` is provided, we just use that, with `zoom`.

  const boundsCenter: [number, number] | null = useMemo(() => {
    if (!bounds) { return null; }

    const [minLat, minLng, maxLat, maxLng] = bounds.flat();
    const lat = (minLat + maxLat) / 2;
    const lng = (minLng + maxLng) / 2;
    return [lat, lng];
  }, [bounds]);

  const pointToCheck: [number, number] = useMemo(() => {
    const result = boundsCenter || center;
    /* NOTE: This can't actually happen because of the guard earlier,
       but useMemo hides that from the compiler, so we have to
       reinforce it. :eyeroll: */
    if (!result) throw new Error('Either `bounds`   or `center` must be provided');
    return result;
  }, [boundsCenter, center]);

  const handleResetView = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds);
    } else {
      map.setView(pointToCheck, zoom);
    }
    e.preventDefault();
  }

  const stillVisible = useIsPointVisible(map, pointToCheck);

  return (
    !stillVisible && (
      <div className={controlClassName}>
        <div className="leaflet-control">
          <div
            className="px-1 pt-1 mb-3 transition-all duration-200 rounded-md shadow-md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              color: 'rgba(59, 130, 246, 0.5)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
              e.currentTarget.style.color = 'rgba(59, 130, 246, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';
              e.currentTarget.style.color = 'rgba(59, 130, 246, 0.5)';
            }}
          >
            <button
              className="cursor-pointer leaflet-control-zoom-reset"
              title="Reset view"
              role="button"
              aria-label="Reset zoom"
              onClick={handleResetView}
            >
              <Undo size={24} className="transition-all duration-200 rounded-md" />
            </button>
          </div>
        </div>
      </div>
    )
  )
}

export { ResetViewControl };
